import type { CSSProperties } from "react";
import { COULEURS, POLICE_TITRE_SPLASH, POLICE_UI_SPLASH } from "../splashStyles.js";

/**
 * Les valeurs du handoff `design_handoff_credits`, réunies : tokens Écrin (`splashStyles`),
 * verre dépoli du groupe des langues, du menu et de la feuille.
 */

const CREME = COULEURS.creme;
const SAUGE = COULEURS.sauge;
const FILET = "rgba(246,238,227,.14)";

/** Le groupe en verre dépoli du haut à droite (langues + ⓘ). */
export const groupeVerre: CSSProperties = {
  display: "flex",
  alignItems: "center",
  background: "rgba(16,26,23,.66)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  pointerEvents: "auto",
};

/** ⓘ de 44 px ; ouvert : fond crème, icône verte. */
export const boutonInfo = (ouvert: boolean): CSSProperties => ({
  appearance: "none",
  width: 44,
  height: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  flex: "none",
  cursor: "pointer",
  border: "none",
  borderRadius: 2,
  background: ouvert ? CREME : "transparent",
  color: ouvert ? COULEURS.vert : CREME,
  pointerEvents: "auto",
});

/** ⋯ de 52 px, collé à droite de la barre de navigation du bas ; un filet de 32 px le sépare. */
export const boutonPlus: CSSProperties = {
  appearance: "none",
  position: "relative",
  width: 52,
  alignSelf: "stretch",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  flex: "none",
  cursor: "pointer",
  border: "none",
  background: "transparent",
  color: CREME,
  pointerEvents: "auto",
};

export const filetVertical: CSSProperties = {
  position: "absolute",
  left: 0,
  top: "50%",
  height: 32,
  width: 1,
  marginTop: -16,
  background: "rgba(246,238,227,.2)",
  pointerEvents: "none",
};

/** Le conteneur des surcouches : couvre la visite, ne capte rien lui-même. */
export const hote: CSSProperties = { position: "absolute", inset: 0, zIndex: 40, pointerEvents: "none" };

export const menu: CSSProperties = {
  position: "absolute",
  width: 320,
  maxWidth: "calc(100% - 24px)",
  boxSizing: "border-box",
  padding: "18px 8px 8px",
  background: "rgba(16,26,23,.9)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(246,238,227,.18)",
  borderRadius: 2,
  boxShadow: "0 16px 40px rgba(8,14,12,.45)",
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  pointerEvents: "auto",
};

export const titreMenu: CSSProperties = {
  margin: 0,
  padding: "0 10px 12px",
  fontFamily: POLICE_TITRE_SPLASH,
  fontWeight: 400,
  fontSize: 19,
  lineHeight: 1.2,
  color: CREME,
};

/** Une action : 44 px (menu) ou 56 px (feuille), icône 20 px, libellé Raleway. */
export const ligne = (hauteur: number, taille: number): CSSProperties => ({
  appearance: "none",
  width: "100%",
  minHeight: hauteur,
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "0 10px",
  boxSizing: "border-box",
  background: "transparent",
  border: "none",
  borderRadius: 2,
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: taille,
  textAlign: "left",
  cursor: "pointer",
});

export const raccourci: CSSProperties = {
  marginLeft: "auto",
  minWidth: 22,
  padding: "2px 6px",
  boxSizing: "border-box",
  textAlign: "center",
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 11.5,
  fontWeight: 600,
  color: "#CFC4B2",
  border: "1px solid rgba(246,238,227,.28)",
  borderRadius: 3,
};

export const signature: CSSProperties = {
  marginTop: 8,
  padding: "14px 10px 6px",
  borderTop: `1px solid ${FILET}`,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

export const signatureFaite: CSSProperties = { fontSize: 12.5, color: "#CFC4B2", letterSpacing: ".02em" };
export const signaturePar: CSSProperties = { fontSize: 13, color: "#EDE4D5", lineHeight: 1.4 };

export const lien: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  gap: 6,
  minHeight: 44,
  color: SAUGE,
  fontSize: 13,
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

/* --- feuille mobile --------------------------------------------------------- */

export const voile = (alpha: number): CSSProperties => ({
  position: "absolute",
  inset: 0,
  background: `rgba(11,19,16,${alpha})`,
  pointerEvents: "auto",
});

export const feuille: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: "88%",
  overflowY: "auto",
  boxSizing: "border-box",
  padding: "0 16px calc(12px + env(safe-area-inset-bottom))",
  background: "rgba(16,26,23,.94)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  borderTop: "1px solid rgba(246,238,227,.22)",
  borderRadius: "10px 10px 0 0",
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  pointerEvents: "auto",
};

export const zonePoignee: CSSProperties = {
  margin: "0 -16px",
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  touchAction: "none",
  cursor: "grab",
};

export const poignee: CSSProperties = { width: 40, height: 4, borderRadius: 2, background: "rgba(246,238,227,.35)" };

export const titreFeuille: CSSProperties = {
  margin: "0 0 14px",
  fontFamily: POLICE_TITRE_SPLASH,
  fontWeight: 400,
  fontSize: 22,
  lineHeight: 1.2,
  color: CREME,
};

export const rangeeLangue: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  minHeight: 56,
  padding: "0 4px",
  fontSize: 15,
};

export const segmente: CSSProperties = {
  display: "flex",
  border: "1px solid rgba(246,238,227,.3)",
  borderRadius: 3,
  overflow: "hidden",
};

export const choixLangue = (actif: boolean): CSSProperties => ({
  appearance: "none",
  width: 52,
  height: 40,
  border: "none",
  cursor: "pointer",
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: ".14em",
  background: actif ? CREME : "transparent",
  color: actif ? COULEURS.vert : CREME,
});

/* --- crédits ---------------------------------------------------------------- */

export const creditsVoile: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  boxSizing: "border-box",
  background: "rgba(11,19,16,.6)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  pointerEvents: "auto",
};

export const creditsFenetre: CSSProperties = {
  position: "relative",
  width: 480,
  maxWidth: "100%",
  maxHeight: "100%",
  overflowY: "auto",
  boxSizing: "border-box",
  padding: "40px 36px 32px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 22,
  textAlign: "center",
  background: "rgba(16,26,23,.94)",
  border: "1px solid rgba(246,238,227,.2)",
  borderRadius: 2,
  boxShadow: "0 24px 60px rgba(8,14,12,.5)",
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
};

export const creditsPlein: CSSProperties = {
  position: "absolute",
  inset: 0,
  overflowY: "auto",
  boxSizing: "border-box",
  padding: "calc(8px + env(safe-area-inset-top)) 20px calc(28px + env(safe-area-inset-bottom))",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 22,
  textAlign: "center",
  background: COULEURS.fond,
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  pointerEvents: "auto",
};

export const entetePlein: CSSProperties = {
  alignSelf: "stretch",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 52,
};

export const boutonRetour: CSSProperties = {
  appearance: "none",
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "0 10px 0 4px",
  background: "transparent",
  border: "none",
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 15,
  cursor: "pointer",
};

export const croix: CSSProperties = {
  appearance: "none",
  width: 44,
  height: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  borderRadius: 2,
  color: CREME,
  cursor: "pointer",
};

export const croixFenetre: CSSProperties = { ...croix, position: "absolute", top: 8, right: 8 };

export const titreCredits: CSSProperties = {
  margin: 0,
  fontFamily: POLICE_TITRE_SPLASH,
  fontWeight: 400,
  fontSize: 40,
  letterSpacing: ".04em",
};

export const listeCredits: CSSProperties = { alignSelf: "stretch", display: "flex", flexDirection: "column" };

export const ligneCredit: CSSProperties = {
  padding: "14px 0",
  borderTop: `1px solid ${FILET}`,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

export const roleCredit: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: "#BFD0BA",
};

export const nomCredit: CSSProperties = { fontSize: 15, color: CREME };

/** Le bouton lien crème de 48 px. */
export const boutonLienCreme: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  minHeight: 48,
  padding: "0 28px",
  boxSizing: "border-box",
  background: CREME,
  color: COULEURS.vert,
  borderRadius: 2,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: ".08em",
  textDecoration: "none",
};
